import { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { forwardRef } from "react";

import Box from "@mui/material/Box";

/**
 * Canvas signature pad.
 *
 * Strokes are smoothed with quadratic curves through the midpoints of the
 * sampled points, and the line width tapers with pointer velocity so the result
 * looks like ink rather than a constant-width polyline. Pointer events cover
 * mouse, touch and stylus with one code path; `touch-action: none` stops the
 * page from scrolling while signing on a phone.
 *
 * Exposes `isEmpty()`, `clear()`, `undo()` and `toDataURL()` via ref.
 */
const SignaturePad = forwardRef(
  ({ height = 200, penColor = "#0e3461", onChange, disabled = false }, ref) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);

    // Completed strokes, kept so the canvas can be replayed after a resize or
    // an undo. Each stroke is an array of { x, y, width } in CSS pixels.
    const strokesRef = useRef([]);
    const currentStrokeRef = useRef(null);
    const lastPointRef = useRef(null);
    const lastWidthRef = useRef(2);
    const drawingRef = useRef(false);

    const [isEmpty, setIsEmpty] = useState(true);

    const getContext = () => canvasRef.current?.getContext("2d");

    const drawStroke = useCallback(
      (context, stroke) => {
        if (stroke.length < 2) {
          const point = stroke[0];
          if (!point) return;
          context.beginPath();
          context.arc(point.x, point.y, point.width / 2, 0, Math.PI * 2);
          context.fillStyle = penColor;
          context.fill();
          return;
        }

        context.strokeStyle = penColor;
        context.lineCap = "round";
        context.lineJoin = "round";

        for (let index = 1; index < stroke.length; index += 1) {
          const previous = stroke[index - 1];
          const point = stroke[index];
          const midX = (previous.x + point.x) / 2;
          const midY = (previous.y + point.y) / 2;

          context.beginPath();
          context.lineWidth = point.width;
          if (index === 1) {
            context.moveTo(previous.x, previous.y);
          } else {
            const beforePrevious = stroke[index - 2];
            context.moveTo(
              (beforePrevious.x + previous.x) / 2,
              (beforePrevious.y + previous.y) / 2
            );
          }
          context.quadraticCurveTo(previous.x, previous.y, midX, midY);
          context.stroke();
        }
      },
      [penColor]
    );

    const redraw = useCallback(() => {
      const canvas = canvasRef.current;
      const context = getContext();
      if (!canvas || !context) return;

      const ratio = window.devicePixelRatio || 1;
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.scale(ratio, ratio);

      strokesRef.current.forEach((stroke) => drawStroke(context, stroke));
    }, [drawStroke]);

    // Match the backing store to the CSS size so strokes stay crisp on retina
    // screens and survive orientation changes.
    useEffect(() => {
      const resize = () => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const ratio = window.devicePixelRatio || 1;
        const width = container.clientWidth;
        canvas.width = Math.max(width * ratio, 1);
        canvas.height = height * ratio;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        redraw();
      };

      resize();
      window.addEventListener("resize", resize);
      window.addEventListener("orientationchange", resize);
      return () => {
        window.removeEventListener("resize", resize);
        window.removeEventListener("orientationchange", resize);
      };
    }, [height, redraw]);

    const getPoint = (event) => {
      const rect = canvasRef.current.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    const notify = useCallback(() => {
      const empty = strokesRef.current.length === 0;
      setIsEmpty(empty);
      onChange?.(!empty);
    }, [onChange]);

    const handlePointerDown = (event) => {
      if (disabled) return;
      event.preventDefault();
      canvasRef.current.setPointerCapture?.(event.pointerId);

      drawingRef.current = true;
      const point = getPoint(event);
      lastPointRef.current = { ...point, time: Date.now() };
      lastWidthRef.current = 2.4;
      currentStrokeRef.current = [{ ...point, width: 2.4 }];
      strokesRef.current.push(currentStrokeRef.current);
      redraw();
    };

    const handlePointerMove = (event) => {
      if (!drawingRef.current || disabled) return;
      event.preventDefault();

      const point = getPoint(event);
      const previous = lastPointRef.current;
      const now = Date.now();
      const distance = Math.hypot(point.x - previous.x, point.y - previous.y);

      // Ignore sub-pixel jitter so the smoothing has room to work.
      if (distance < 1.2) return;

      const velocity = distance / Math.max(now - previous.time, 1);
      const targetWidth = Math.max(1.1, Math.min(3.2, 3.2 - velocity * 2.2));
      // Ease towards the target so the width never jumps between samples.
      const width = lastWidthRef.current * 0.65 + targetWidth * 0.35;

      currentStrokeRef.current.push({ ...point, width });
      lastPointRef.current = { ...point, time: now };
      lastWidthRef.current = width;
      redraw();
    };

    const endStroke = (event) => {
      if (!drawingRef.current) return;
      event?.preventDefault();
      drawingRef.current = false;
      currentStrokeRef.current = null;
      notify();
    };

    useImperativeHandle(ref, () => ({
      isEmpty: () => strokesRef.current.length === 0,
      clear: () => {
        strokesRef.current = [];
        redraw();
        notify();
      },
      undo: () => {
        strokesRef.current.pop();
        redraw();
        notify();
      },
      toDataURL: () => canvasRef.current?.toDataURL("image/png"),
    }));

    return (
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          width: "100%",
          borderRadius: 1.5,
          border: "1px dashed",
          borderColor: isEmpty ? "grey.400" : "primary.main",
          bgcolor: "#fff",
          overflow: "hidden",
          transition: "border-color 0.2s",
        }}
      >
        <Box
          component="canvas"
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endStroke}
          onPointerLeave={endStroke}
          onPointerCancel={endStroke}
          sx={{
            display: "block",
            touchAction: "none",
            cursor: disabled ? "not-allowed" : "crosshair",
          }}
        />

        {/* Signing baseline — mirrors the line on a paper form. */}
        <Box
          sx={{
            position: "absolute",
            left: 24,
            right: 24,
            bottom: 42,
            borderBottom: "1px solid",
            borderColor: "grey.300",
            pointerEvents: "none",
          }}
        />
      </Box>
    );
  }
);

SignaturePad.displayName = "SignaturePad";

export default SignaturePad;
